import React, { useState } from 'react';
import { FaTimes, FaEdit, FaEnvelope, FaCheck } from 'react-icons/fa';
import './QuoteRequestModal.css';
import portfolioAnalytics from '../utils/analytics';

const QuoteRequestModal = ({ isOpen, onClose, selectedPackage, selectedServices }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    projectDetails: '',
    timeline: '',
    budget: ''
  });

  const [emailTemplate, setEmailTemplate] = useState('');
  const [isEditingTemplate, setIsEditingTemplate] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Generate default email template based on package and services
  React.useEffect(() => {
    if (selectedPackage && selectedServices) {
      const servicePath = selectedServices.includes('digital-marketing') && selectedServices.includes('web-development') 
        ? 'Full-Stack Digital Solutions' 
        : selectedServices.includes('digital-marketing') 
          ? 'Digital Marketing' 
          : 'Web Development';

      const defaultTemplate = `Subject: Custom Quote Request - ${selectedPackage.name}

Dear Harish,

I'm interested in getting a custom quote for the "${selectedPackage.name}" package for my ${servicePath.toLowerCase()} needs.

Here are my project details:

Project Overview:
[Please describe your project goals and requirements]

Business Information:
- Company: [Your company name]
- Industry: [Your industry]
- Current Situation: [Brief description of your current setup]

Specific Requirements:
${selectedPackage.features.map(feature => `• ${feature}`).join('\n')}

Timeline: [When do you need this completed?]
Budget Range: [Your approximate budget range]

I'm looking forward to discussing how we can work together to achieve my business goals.

Best regards,
[Your Name]

---
This message was sent via the quote request system at portfolioweb.fliphatmedia.com`;

      setEmailTemplate(defaultTemplate);
    }
  }, [selectedPackage, selectedServices]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTemplateChange = (e) => {
    setEmailTemplate(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Track quote request
      portfolioAnalytics.logEvent({
        type: 'quote_request_submitted',
        timestamp: Date.now(),
        data: {
          packageName: selectedPackage.name,
          packageCategory: selectedPackage.category,
          selectedServices,
          userEmail: formData.email,
          hasCustomTemplate: isEditingTemplate
        }
      });

      // Determine API endpoint based on environment
      const isDevelopment = window.location.hostname === 'localhost';
      const apiEndpoint = isDevelopment 
        ? 'http://localhost:8080/api/send-quote-request.php'
        : '/api/send-quote-request.php';
      
      console.log('Submitting quote request to:', apiEndpoint);
      
      // Send quote request via backend
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          // User details
          userDetails: formData,
          // Package information
          packageInfo: {
            name: selectedPackage.name,
            category: selectedPackage.category,
            features: selectedPackage.features,
            duration: selectedPackage.duration
          },
          // Service selection
          selectedServices,
          // Custom email template
          emailTemplate: emailTemplate,
          // Metadata
          metadata: {
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            referrer: document.referrer
          }
        })
      });

      if (response.ok) {
        setSubmitSuccess(true);
        setTimeout(() => {
          onClose();
          setSubmitSuccess(false);
          // Reset form
          setFormData({
            name: '',
            email: '',
            company: '',
            phone: '',
            projectDetails: '',
            timeline: '',
            budget: ''
          });
        }, 2000);
      } else {
        throw new Error('Failed to send quote request');
      }
    } catch (error) {
      console.error('Quote request error:', error);
      
      // Check if we're in development mode
      const isDevelopment = window.location.hostname === 'localhost';
      
      if (isDevelopment) {
        // In development, show success and demo the data
        console.log('Development Mode - Quote Request Data:', {
          userDetails: formData,
          packageInfo: {
            name: selectedPackage.name,
            category: selectedPackage.category,
            features: selectedPackage.features,
            duration: selectedPackage.duration
          },
          selectedServices,
          emailTemplate
        });
        
        alert(`Development Mode: Quote request would be sent in production.\n\nPackage: ${selectedPackage.name}\nClient: ${formData.name} (${formData.email})\nProject: ${formData.projectDetails}\n\nIn production, both you and the client would receive professional emails!`);
        
        // Show success for demo
        setSubmitSuccess(true);
        setTimeout(() => {
          onClose();
          setSubmitSuccess(false);
          // Reset form
          setFormData({
            name: '',
            email: '',
            company: '',
            phone: '',
            projectDetails: '',
            timeline: '',
            budget: ''
          });
        }, 2000);
      } else {
        alert('There was an error sending your quote request. Please try again or contact us directly.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="quote-modal-overlay" onClick={onClose}>
      <div className="quote-modal" onClick={(e) => e.stopPropagation()}>
        <div className="quote-modal-header">
          <h3>Request Custom Quote</h3>
          <button className="close-btn" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {submitSuccess ? (
          <div className="success-message">
            <FaCheck className="success-icon" />
            <h4>Quote Request Sent Successfully!</h4>
            <p>Thank you for your interest. You'll receive a confirmation email shortly, and I'll get back to you within 24 hours with a personalized quote.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="quote-form">
            <div className="package-summary">
              <h4>📦 {selectedPackage?.name}</h4>
              <p>{selectedPackage?.subtitle}</p>
              <div className="package-features-preview">
                <strong>Includes:</strong>
                <ul>
                  {selectedPackage?.features.slice(0, 3).map((feature, index) => (
                    <li key={index}>{feature}</li>
                  ))}
                  {selectedPackage?.features.length > 3 && (
                    <li>+ {selectedPackage.features.length - 3} more features</li>
                  )}
                </ul>
              </div>
            </div>

            <div className="form-section">
              <h4>Your Details</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Company</label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h4>Project Requirements</h4>
              <div className="form-group">
                <label>Project Details *</label>
                <textarea
                  name="projectDetails"
                  value={formData.projectDetails}
                  onChange={handleInputChange}
                  placeholder="Describe your project goals, requirements, and what you're looking to achieve..."
                  rows="4"
                  required
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Preferred Timeline</label>
                  <select
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleInputChange}
                  >
                    <option value="">Select timeline</option>
                    <option value="asap">ASAP (Rush project)</option>
                    <option value="1-2weeks">1-2 weeks</option>
                    <option value="3-4weeks">3-4 weeks</option>
                    <option value="1-2months">1-2 months</option>
                    <option value="flexible">Flexible</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Budget Range (Optional)</label>
                  <select
                    name="budget"
                    value={formData.budget}
                    onChange={handleInputChange}
                  >
                    <option value="">Select budget range</option>
                    <option value="under-2k">Under $2,000</option>
                    <option value="2k-5k">$2,000 - $5,000</option>
                    <option value="5k-10k">$5,000 - $10,000</option>
                    <option value="10k-20k">$10,000 - $20,000</option>
                    <option value="20k-plus">$20,000+</option>
                    <option value="discuss">Let's discuss</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="form-section">
              <div className="email-template-header">
                <h4><FaEnvelope /> Email Template</h4>
                <button
                  type="button"
                  className="edit-template-btn"
                  onClick={() => setIsEditingTemplate(!isEditingTemplate)}
                >
                  <FaEdit /> {isEditingTemplate ? 'Use Default' : 'Customize Message'}
                </button>
              </div>
              
              {isEditingTemplate ? (
                <div className="form-group">
                  <label>Customize your message (optional)</label>
                  <textarea
                    value={emailTemplate}
                    onChange={handleTemplateChange}
                    className="email-template-editor"
                    rows="12"
                    placeholder="Edit your message..."
                  />
                </div>
              ) : (
                <div className="email-template-preview">
                  <p>A professional quote request will be sent with your details and requirements.</p>
                  <small>Click "Customize Message" if you want to personalize the email.</small>
                </div>
              )}
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-outline" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send Quote Request'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default QuoteRequestModal;
